import { Component } from '@theme/component';

/**
 * <q-tabs> — accessible tabs for custom sections (product Description /
 * Specifications / Reviews today; reusable anywhere).
 *
 * Follows the WAI-ARIA "Tabs with automatic activation" pattern:
 * one tab stop, ←/→ (mirrored in RTL), Home/End move and activate.
 * The markup already renders the first tab selected and the other panels
 * `hidden`, so nothing jumps while this module loads.
 *
 * Deep links: a link to #product-<key> anywhere on the page (e.g. the
 * rating line's "126 reviews") opens that tab and scrolls to the tabs; the
 * same works when the page loads with that hash.
 *
 * A panel that holds a `[data-tab-focus]` element when the page loads (the
 * "review sent" / "could not send" message after a form post) is opened and
 * that element is brought into view and focused.
 *
 * Markup contract (refs, Horizon's Component pattern):
 *   ref="tabs[]"    role="tab" buttons   on:click="/select/N" (N = 0-based)
 *                                        on:keydown="/onKeydown"
 *                                        data-key="reviews" (for deep links)
 *   ref="panels[]"  role="tabpanel", same order as the tabs
 *
 * @extends {Component<{ tabs: HTMLButtonElement[], panels: HTMLElement[] }>}
 */
class Tabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onLinkClick);
    window.addEventListener('hashchange', this.#onHashChange);

    this.select(0, { focus: false });

    const result = this.querySelector('[data-tab-focus]');
    const resultPanel = result ? this.refs.panels.findIndex((panel) => panel.contains(result)) : -1;
    if (result instanceof HTMLElement && resultPanel !== -1) {
      this.select(resultPanel, { focus: false });
      requestAnimationFrame(() => {
        // The message itself, not the tab strip: on phones it sits below the summary
        result.scrollIntoView({ block: 'center' });
        result.focus({ preventScroll: true });
      });
      return;
    }

    if (!this.#openFromHash(location.hash)) return;
    // Opened from a deep link: bring the tabs into view once layout settles
    requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onLinkClick);
    window.removeEventListener('hashchange', this.#onHashChange);
  }

  /**
   * @param {string} hash - "#product-<key>"
   * @returns {boolean} whether a tab matched
   */
  #openFromHash(hash) {
    if (!hash.startsWith('#product-')) return false;
    const key = hash.slice('#product-'.length);
    const index = this.refs.tabs.findIndex((tab) => tab.dataset.key === key);
    if (index === -1) return false;
    this.select(index, { focus: false });
    return true;
  }

  /** @param {MouseEvent} event */
  #onLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#product-"]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.#openFromHash(link.hash)) return;
    event.preventDefault();
    history.replaceState(null, '', link.hash);
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'instant' });
  };

  #onHashChange = () => {
    if (this.#openFromHash(location.hash)) this.scrollIntoView({ block: 'start' });
  };

  /**
   * @param {number} index - also the inline data of on:click="/select/N"
   * @param {{ focus?: boolean } | Event} [options]
   */
  select(index, options) {
    const { tabs, panels } = this.refs;
    if (!tabs[index]) return;

    this.#index = index;
    tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    const focus = options instanceof Event ? false : options?.focus ?? true;
    if (focus) tabs[index]?.focus();
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    const count = this.refs.tabs.length;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';

    /** @type {Record<string, number>} */
    const moves = {
      [forward]: (this.#index + 1) % count,
      [back]: (this.#index - 1 + count) % count,
      Home: 0,
      End: count - 1,
    };

    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(next);
  }
}

if (!customElements.get('q-tabs')) {
  customElements.define('q-tabs', Tabs);
}
